import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SystemProductImage, productImageUrl } from 'utils/system-products';

type ProductImageCarouselProps = Readonly<{
  products: SystemProductImage[];
  thumbSize?: 'sm' | 'md';
}>;

type CarouselDimensions = {
  containerHeight: string;
  gridThumb: string;
  slideImage: string;
};

const DIMENSIONS_BY_THUMB_SIZE: Record<
  NonNullable<ProductImageCarouselProps['thumbSize']>,
  CarouselDimensions
> = {
  sm: {
    containerHeight: 'h-24',
    gridThumb: 'h-10 w-12',
    slideImage: 'h-16 w-20'
  },
  md: {
    containerHeight: 'h-32',
    gridThumb: 'h-14 w-16',
    slideImage: 'h-20 w-24'
  }
};

function gridColumnsClass(productCount: number): string {
  if (productCount <= 1) return 'grid-cols-1';
  if (productCount === 2) return 'grid-cols-2';
  if (productCount === 3) return 'grid-cols-3';
  if (productCount === 4) return 'grid-cols-2';
  return 'grid-cols-3';
}

export default function ProductImageCarousel({
  products,
  thumbSize = 'md'
}: ProductImageCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const productsSignature = products.map((item) => item.key).join('|');
  const [prevProductsSignature, setPrevProductsSignature] =
    useState(productsSignature);
  if (productsSignature !== prevProductsSignature) {
    setPrevProductsSignature(productsSignature);
    setCurrentSlide(0);
  }

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const goToSlide = useCallback(
    (slideIndex: number) => setCurrentSlide(slideIndex),
    []
  );

  if (products.length === 0) return null;

  const dimensions = DIMENSIONS_BY_THUMB_SIZE[thumbSize];
  const totalSlides = products.length + 1;
  const showNav = products.length > 1;
  const slideWidth = containerWidth || 0;

  return (
    <div className='relative'>
      <div
        ref={containerRef}
        className={clsx(dimensions.containerHeight, 'overflow-hidden')}
      >
        <div
          className='flex h-full transition-transform duration-300 ease-in-out'
          style={{
            transform: slideWidth
              ? `translateX(-${currentSlide * slideWidth}px)`
              : undefined
          }}
        >
          <div
            className='flex h-full shrink-0 items-center justify-center'
            style={{ width: slideWidth || '100%' }}
          >
            <div
              className={clsx('grid gap-1', gridColumnsClass(products.length))}
            >
              {products.slice(0, 6).map((item) => (
                <img
                  key={`grid-${item.key}`}
                  className={clsx(dimensions.gridThumb, 'object-contain')}
                  src={productImageUrl(item.product.photo)}
                  alt={item.product.name}
                />
              ))}
            </div>
          </div>
          {products.map((item) => (
            <div
              key={`slide-${item.key}`}
              className='flex h-full shrink-0 items-center justify-center'
              style={{ width: slideWidth || '100%' }}
            >
              <img
                className={clsx(dimensions.slideImage, 'object-contain')}
                src={productImageUrl(item.product.photo)}
                alt={item.product.name}
              />
            </div>
          ))}
        </div>
      </div>

      {showNav && (
        <>
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              goToSlide((currentSlide - 1 + totalSlides) % totalSlides);
            }}
            className='absolute left-1 top-1/2 z-10 -translate-y-1/2 rounded-full bg-gray-100 p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
          >
            <ChevronLeftIcon className='h-4 w-4' />
          </button>
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              goToSlide((currentSlide + 1) % totalSlides);
            }}
            className='absolute right-1 top-1/2 z-10 -translate-y-1/2 rounded-full bg-gray-100 p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
          >
            <ChevronRightIcon className='h-4 w-4' />
          </button>
          <div className='absolute bottom-1 left-1/2 z-10 flex -translate-x-1/2 gap-1'>
            {Array.from({ length: totalSlides }, (_, i) => i).map(
              (slideIndex) => (
                <button
                  key={`dot-${slideIndex}`}
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation();
                    goToSlide(slideIndex);
                  }}
                  className={clsx(
                    'h-1.5 rounded-full transition-all',
                    slideIndex === currentSlide
                      ? 'w-3 bg-blue-500'
                      : 'w-1.5 bg-gray-300 hover:bg-gray-400'
                  )}
                />
              )
            )}
          </div>
        </>
      )}
    </div>
  );
}
